import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { useCallHearingParticipantHandlers } from 'features/call/call-accepted/hooks';
import {
  AddCallee,
  AddHearingConnecting,
  CallDetailsButtonAddHearing,
} from 'features/call/call-details/components';
import { AddHearingStatus } from 'features/call/call-details/enums';
import {
  addHearingStatusSelector,
  setAddHearingStatus,
} from 'features/call/call-details/store';
import { useHearings } from 'features/call/call-hearing/hooks';
import {
  primaryHearingStatusSelector,
  selectLastSecondaryConnectingHearing,
} from 'features/call/call-hearing/store';
import { isEmergencyOrCallbackSelector } from 'features/call/vrs-call/store';
import { shouldCallControlsBeVisibleSelector } from 'features/spawn-call/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { useHearingCancel } from 'features/voice-meeting/hooks';

export const CallDetailsAddHearing = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'addCallee',
  });
  const dispatch = useAppDispatch();
  const hasObservedConnectingHearing = useRef(false);

  const addHearingStatus = useAppSelector(addHearingStatusSelector);
  const lastSecondaryConnectingHearing = useAppSelector(
    selectLastSecondaryConnectingHearing
  );
  const primaryHearingStatus = useAppSelector(primaryHearingStatusSelector);
  const shouldCallControlsBeVisible = useAppSelector(
    shouldCallControlsBeVisibleSelector
  );
  const isEmergencyOrCallback = useAppSelector(isEmergencyOrCallbackSelector);
  const { emergencySecondHearing } = useFlags();
  const canStartSecondHearing =
    !isEmergencyOrCallback || emergencySecondHearing === true;

  const { handleRegularHearingCall } = useCallHearingParticipantHandlers();
  const { cancelHearing } = useHearingCancel();

  const { addHearingIfNotExists } = useHearings();

  const handleAddHearingButton = () => {
    dispatch(setAddHearingStatus(AddHearingStatus.ENTER_PHONE_NUMBER));
  };

  const handleEnterPhoneNumber = async (
    phoneNumber: string,
    phoneExtension = ''
  ) => {
    try {
      dispatch(setAddHearingStatus(AddHearingStatus.CONNECTING));
      await addHearingIfNotExists(phoneNumber, phoneExtension);
      await handleRegularHearingCall(phoneNumber);
    } catch {
      dispatch(setAddHearingStatus(AddHearingStatus.ENTER_PHONE_NUMBER));
    }
  };

  const handleCancel = () => {
    dispatch(setAddHearingStatus(AddHearingStatus.BASE));
  };

  const handleCancelConnecting = async (
    hearing: NonNullable<typeof lastSecondaryConnectingHearing>
  ) => {
    await cancelHearing({
      id: hearing.id || undefined,
      phoneNumber: hearing.phoneNumber,
    });
    dispatch(setAddHearingStatus(AddHearingStatus.BASE));
  };

  useEffect(() => {
    if (addHearingStatus !== AddHearingStatus.CONNECTING) {
      hasObservedConnectingHearing.current = false;
      return;
    }

    if (lastSecondaryConnectingHearing) {
      hasObservedConnectingHearing.current = true;
      return;
    }

    if (hasObservedConnectingHearing.current) {
      hasObservedConnectingHearing.current = false;
      dispatch(setAddHearingStatus(AddHearingStatus.BASE));
    }
  }, [dispatch, addHearingStatus, lastSecondaryConnectingHearing]);

  if (primaryHearingStatus !== 'connected') {
    return null;
  }

  if (!shouldCallControlsBeVisible || !canStartSecondHearing) {
    return null;
  }

  return (
    <Container>
      {addHearingStatus === AddHearingStatus.BASE && (
        <CallDetailsButtonAddHearing onClick={handleAddHearingButton} />
      )}
      {addHearingStatus === AddHearingStatus.ENTER_PHONE_NUMBER && (
        <Wrapper>
          <AddCallee
            onClick={handleEnterPhoneNumber}
            onCancel={handleCancel}
            title={t('addHearingCallee')}
            showExtensionInput
          />
        </Wrapper>
      )}
      {addHearingStatus === AddHearingStatus.CONNECTING &&
        lastSecondaryConnectingHearing && (
          <Wrapper>
            <AddHearingConnecting
              onCancel={handleCancelConnecting}
              hearing={lastSecondaryConnectingHearing}
            />
          </Wrapper>
        )}
    </Container>
  );
};

const Container = styled('div')`
  margin-top: ${theme.spacing(6)};
`;

const Wrapper = styled('div')`
  margin-top: ${theme.spacing(2)};
`;
